/**
 * Proxy Services Module
 * Sprint 36: Non-canister proxy endpoints
 *
 * This module isolates the remaining direct proxy HTTP calls that are NOT
 * canister calls. These endpoints are served by the Python proxy server
 * (canister_proxy.py) and provide access to local-only services:
 *
 * - Ollama LLM (chat, warmup)
 * - Discovery pre-check (proxy health, EC2 status, Hub connectivity)
 *
 * These services only exist in local development mode. On mainnet, they
 * are either unavailable or handled differently.
 */

import { environment } from '../config/environment';

/**
 * Get the proxy server base URL
 */
function getProxyUrl(): string {
    return environment.proxyUrl;
}

// ============================================================================
// Ollama LLM Services
// ============================================================================

/**
 * Ollama chat message format
 */
export interface OllamaChatMessage {
    role: 'system' | 'user' | 'assistant';
    content: string;
}

/**
 * Ollama chat response
 */
export interface OllamaChatResult {
    message: string;
    conversation_id: string;
    backend_used: string;
    response_time_ms: number;
}

/**
 * Send a chat message to the local Ollama LLM via proxy
 *
 * @param messages - Conversation history
 * @param conversationId - Optional conversation ID for context
 * @param model - Model name (resolved by caller via settings)
 * @returns Chat result with response text and metadata
 * @throws Error if the proxy or Ollama is unavailable
 */
export async function ollamaChat(
    messages: OllamaChatMessage[],
    conversationId?: string,
    model?: string
): Promise<OllamaChatResult> {
    const response = await fetch(`${getProxyUrl()}/ollama/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            messages,
            ...(model ? { model } : {}),
            conversation_id: conversationId
        })
    });

    const result = await response.json();

    if ('Ok' in result) {
        return {
            message: result.Ok.message,
            conversation_id: result.Ok.conversation_id,
            backend_used: result.Ok.backend_used,
            response_time_ms: result.Ok.response_time_ms
        };
    } else {
        throw new Error(result.Err || 'Ollama chat request failed');
    }
}

/**
 * Warm up the Ollama model to reduce first-response latency.
 * This is a fire-and-forget operation — failures are non-critical.
 *
 * @param model - Model name (resolved by caller via settings)
 * @returns Load time in milliseconds, or null if warmup failed
 */
export async function ollamaWarmup(model?: string): Promise<number | null> {
    try {
        const response = await fetch(`${getProxyUrl()}/ollama/warmup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(model ? { model } : {})
        });

        const result = await response.json();

        if ('Ok' in result) {
            return result.Ok.load_time_ms;
        } else {
            console.warn(`Ollama warmup issue: ${result.Err}`);
            return null;
        }
    } catch (error) {
        console.warn('Ollama warmup failed (non-critical):', error instanceof Error ? error.message : error);
        return null;
    }
}

// ============================================================================
// Discovery Pre-Check Service
// ============================================================================

/**
 * Pre-discovery check result
 */
export interface PreCheckResult {
    success: boolean;
    data?: {
        timestamp: string;
        checks: {
            proxy?: { status: string; port?: string };
            ec2?: { status: string; region?: string };
            discovery_hub?: { status: string; url?: string };
        };
        errors?: string[];
        warnings?: string[];
    };
}

/**
 * Run pre-discovery system checks via the proxy server.
 * Verifies proxy health, EC2 instance status, and Discovery Hub connectivity.
 *
 * Only available in local development mode.
 *
 * @returns Pre-check results with status of each subsystem
 */
export async function discoveryPreCheck(): Promise<PreCheckResult> {
    const response = await fetch(`${getProxyUrl()}/discovery/pre-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });

    return await response.json();
}
